import { useState, useEffect, useCallback } from "react";
import { Panel, Label } from "@/components/SplitLayout";
import CopyButton from "@/components/CopyButton";
import { ArrowClockwise } from "@phosphor-icons/react";

const SETS = {
  upper: "ABCDEFGHJKLMNPQRSTUVWXYZ",
  lower: "abcdefghijkmnopqrstuvwxyz",
  number: "23456789",
  symbol: "!@#$%^&*()-_=+[]{};:,.?",
};

export default function PasswordGenerator() {
  const [length, setLength] = useState(16);
  const [opts, setOpts] = useState({ upper: true, lower: true, number: true, symbol: true });
  const [password, setPassword] = useState("");

  const generate = useCallback(() => {
    let pool = "";
    Object.keys(opts).forEach((k) => {
      if (opts[k]) pool += SETS[k];
    });
    if (!pool) {
      setPassword("");
      return;
    }
    const arr = new Uint32Array(length);
    crypto.getRandomValues(arr);
    let pw = "";
    for (let i = 0; i < length; i++) pw += pool[arr[i] % pool.length];
    setPassword(pw);
  }, [length, opts]);

  useEffect(() => {
    generate();
  }, [generate]);

  const activeSets = Object.values(opts).filter(Boolean).length;
  const strength = (() => {
    let score = 0;
    if (length >= 8) score++;
    if (length >= 12) score++;
    if (length >= 20) score++;
    if (activeSets >= 3) score++;
    if (opts.symbol) score++;
    return Math.min(score, 4);
  })();
  const strengthMeta = [
    { label: "Very weak", color: "bg-red-500" },
    { label: "Weak", color: "bg-orange-500" },
    { label: "Fair", color: "bg-yellow-500" },
    { label: "Strong", color: "bg-green-500" },
    { label: "Very strong", color: "bg-green-600" },
  ][strength];

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div className="space-y-6">
        <Panel title="Settings">
          <div className="space-y-6">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Length</Label>
                <span className="font-mono text-sm font-bold text-primary">{length}</span>
              </div>
              <input
                data-testid="password-length"
                type="range"
                min={4}
                max={48}
                value={length}
                onChange={(e) => setLength(+e.target.value)}
                className="w-full accent-[hsl(var(--primary))]"
              />
            </div>
            <div className="space-y-3">
              <Label>Include</Label>
              {[
                { k: "upper", label: "Uppercase (A-Z)" },
                { k: "lower", label: "Lowercase (a-z)" },
                { k: "number", label: "Numbers (0-9)" },
                { k: "symbol", label: "Symbols (!@#)" },
              ].map((o) => (
                <label key={o.k} className="flex items-center gap-3 text-sm">
                  <input
                    data-testid={`password-opt-${o.k}`}
                    type="checkbox"
                    checked={opts[o.k]}
                    onChange={(e) => setOpts((p) => ({ ...p, [o.k]: e.target.checked }))}
                    className="h-4 w-4 accent-[hsl(var(--primary))]"
                  />
                  {o.label}
                </label>
              ))}
            </div>
            <button
              data-testid="password-generate"
              onClick={generate}
              className="inline-flex w-full items-center justify-center gap-2 bg-primary px-5 py-3 text-xs font-bold uppercase tracking-[0.12em] text-primary-foreground transition-transform duration-150 hover:-translate-y-0.5 active:scale-[0.98]"
            >
              <ArrowClockwise size={16} weight="bold" /> Regenerate
            </button>
          </div>
        </Panel>
      </div>

      <div className="lg:sticky lg:top-24 lg:self-start">
        <Panel title="Your password" testId="password-output" action={<CopyButton text={password} />}>
          <div
            data-testid="password-value"
            className="break-all border border-border bg-background p-5 font-mono text-lg font-bold tracking-tight"
          >
            {password || <span className="text-muted-foreground">Select at least one set</span>}
          </div>
          <div className="mt-5 space-y-2">
            <div className="flex items-center justify-between">
              <Label>Strength</Label>
              <span className="text-xs font-bold">{password ? strengthMeta.label : "—"}</span>
            </div>
            <div className="flex gap-1.5">
              {[0, 1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className={`h-1.5 flex-1 ${
                    password && i <= strength ? strengthMeta.color : "bg-border"
                  }`}
                />
              ))}
            </div>
          </div>
        </Panel>
      </div>
    </div>
  );
}
